import Styles from './TodoAssigneePicker.module.scss'
import cx from 'classnames'
import { TodoAssigneePickerProps } from './Types'
import Avatar from 'common/Avatar/Avatar'
import { TodoAssigneePickerPopoverContent } from './TodoAssigneePickerPopoverContent'
import { getInitials } from 'utils'
import { ReactComponent as AddMemberIcon } from 'assets/icons/addMember16.svg'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Pressable } from '@react-aria/interactions'
import { IconButton } from 'components/IconButton/IconButton'
import { Button } from 'components/Button/Button'
import { Tooltip } from 'components/Tooltip/Tooltip'

const AVATAR_SIZES = {
  xs: 16,
  md: 18,
  lg: 18,
  sm: 20,
  xl: 24,
}

const Trigger = ({
  size = 'md',
  value,
  className,
  labelClassName,
  showLabel,
  hideEmptyLabel,
  preventFocusOnPress = false,
  ...rest
}: Partial<TodoAssigneePickerProps>) => {
  const emptyLabel = hideEmptyLabel ? '' : 'Assign'

  if (size === 'xs') {
    if (!showLabel) {
      if (value) {
        return (
          <IconButton preventFocusOnPress className={cx(className, Styles.assignee)} {...rest} size="sm">
            <Avatar label={getInitials(value.display_name)} size={AVATAR_SIZES[size]} url={value.avatar_url} />
          </IconButton>
        )
      }

      return (
        <IconButton
          preventFocusOnPress
          className={cx(className, Styles.assignee)}
          icon={AddMemberIcon}
          size="sm"
          {...rest}
        />
      )
    }

    return (
      <Button
        {...rest}
        className={className}
        variant="tertiary"
        size="sm"
        data-testid="date-picker-button"
        frontIcon={value ? undefined : AddMemberIcon}
        preventFocusOnPress
      >
        {value && <Avatar label={getInitials(value.display_name)} size={AVATAR_SIZES[size]} url={value.avatar_url} />}
        {(value || !hideEmptyLabel) && <span className={labelClassName}>{value ? value.first_name : emptyLabel}</span>}
      </Button>
    )
  }

  return (
    <Pressable>
      <button
        className={cx(Styles.pillButton, Styles[size], className, Styles.assignee)}
        aria-label={!!value ? 'User avatar' : 'Assign icon'}
      >
        {value ? (
          <Avatar label={getInitials(value.display_name)} size={AVATAR_SIZES[size]} url={value.avatar_url} />
        ) : (
          <AddMemberIcon />
        )}
        {['md', 'lg'].includes(size) && showLabel && (
          <span className={Styles.pillButtonText}>{value ? value.display_name : 'Assign'}</span>
        )}
      </button>
    </Pressable>
  )
}

const TodoAssigneePicker = ({
  value,
  size,
  showLabel,
  hideEmptyLabel,
  onChange,
  placeholder = 'Assign to...',
  onOpenChange,
  className,
  labelClassName,
  placement,
  readOnly,
  forceOpen = false,
  preventFocusOnPress = false,
}: TodoAssigneePickerProps) => {
  if (readOnly) {
    return (
      <Trigger
        size={size}
        value={value}
        className={className}
        labelClassName={labelClassName}
        showLabel={showLabel}
        hideEmptyLabel={hideEmptyLabel}
        aria-label="Assignee"
      />
    )
  }

  return (
    <DropdownTrigger
      onOpenChange={onOpenChange}
      popoverClassName={Styles.popover}
      maxHeight={214}
      placement={placement}
      forceOpen={forceOpen}
      preventFocusOnPress={preventFocusOnPress}
    >
      <Tooltip label={value ? 'Change assignee' : 'Assign'} mouseEnterDelay={1000}>
        <span>
          <Trigger
            size={size}
            value={value}
            className={className}
            labelClassName={labelClassName}
            showLabel={showLabel}
            hideEmptyLabel={hideEmptyLabel}
            aria-label="Assignee"
            preventFocusOnPress={preventFocusOnPress}
          />
        </span>
      </Tooltip>
      {(close) => (
        <TodoAssigneePickerPopoverContent
          value={value}
          placeholder={placeholder}
          onChange={(value) => {
            onChange(value)
            close()
          }}
        />
      )}
    </DropdownTrigger>
  )
}

export { TodoAssigneePicker }
